import { useEffect, useState, useCallback, useRef } from "react";
import {
  Loader2,
  Wifi,
  WifiOff,
  Smartphone,
  Copy,
  Check,
  RefreshCw,
  LogOut,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";
import { mascaraTelefone, apenasDigitos } from "@/lib/masks";
import { useAuth } from "@/context/AuthContext";
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";

type Status = "loading" | "connected" | "disconnected" | "error";

interface ZApiStatus {
  connected: boolean;
  smartphoneConnected?: boolean;
  error?: string;
}

const inputCls =
  "w-full px-3.5 py-2.5 rounded-xl border border-black/10 bg-black/[0.02] text-sm text-black placeholder-black/25 outline-none focus:border-black focus:bg-white transition";

export default function AdminConfiguracoes() {
  const { user } = useAuth();

  // ─── Alterar senha ────────────────────────────────────────────────────────
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [mostraSenhaAtual, setMostraSenhaAtual] = useState(false);
  const [mostraNovaSenha, setMostraNovaSenha] = useState(false);
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  const [erroSenha, setErroSenha] = useState("");
  const [sucessoSenha, setSucessoSenha] = useState(false);

  const handleAlterarSenha = async (e: React.FormEvent) => {
    e.preventDefault();
    setErroSenha("");
    setSucessoSenha(false);

    if (novaSenha.length < 6) {
      setErroSenha("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setErroSenha("As senhas não coincidem.");
      return;
    }
    if (!user?.email) return;

    setSalvandoSenha(true);
    try {
      const credencial = EmailAuthProvider.credential(user.email, senhaAtual);
      await reauthenticateWithCredential(user, credencial);
      await updatePassword(user, novaSenha);
      setSucessoSenha(true);
      setSenhaAtual("");
      setNovaSenha("");
      setConfirmarSenha("");
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setErroSenha("Senha atual incorreta.");
      } else {
        setErroSenha("Erro ao alterar senha. Tente novamente.");
      }
    } finally {
      setSalvandoSenha(false);
    }
  };

  // ─── Z-API ────────────────────────────────────────────────────────────────
  const [status, setStatus] = useState<Status>("loading");
  const [zapiInfo, setZapiInfo] = useState<ZApiStatus | null>(null);

  const [telefone, setTelefone] = useState("");
  const [solicitando, setSolicitando] = useState(false);
  const [pairingCode, setPairingCode] = useState("");
  const [copiado, setCopiado] = useState(false);
  const [erroConexao, setErroConexao] = useState("");

  const [desconectando, setDesconectando] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const verificarStatus = useCallback(async () => {
    try {
      const res = await fetch("/api/zapi/status");
      if (!res.ok) { setStatus("error"); return; }
      const data: ZApiStatus = await res.json();
      setZapiInfo(data);
      setStatus(data.connected ? "connected" : "disconnected");
    } catch {
      setStatus("error");
    }
  }, []);

  // Polling enquanto aguarda conexão
  const iniciarPolling = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      const res = await fetch("/api/zapi/status").catch(() => null);
      if (!res) return;
      const data: ZApiStatus = await res.json().catch(() => null);
      if (data?.connected) {
        setZapiInfo(data);
        setStatus("connected");
        setPairingCode("");
        if (pollingRef.current) clearInterval(pollingRef.current);
      }
    }, 4000);
  }, []);

  useEffect(() => {
    verificarStatus();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [verificarStatus]);

  const handleSolicitarCodigo = async () => {
    setErroConexao("");
    const digitos = apenasDigitos(telefone);
    if (digitos.length < 10) {
      setErroConexao("Informe o DDD + número (ex: (11) 99999-9999).");
      return;
    }
    const numero = `55${digitos}`;

    setSolicitando(true);
    try {
      const res = await fetch("/api/zapi/phone-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: numero }),
      });
      const data = await res.json();

      if (!res.ok) {
        // Mostra a mensagem real da Z-API
        const msg = data.error ?? data.message ?? data.details ?? JSON.stringify(data);
        setErroConexao(`Erro Z-API: ${msg}`);
        return;
      }

      // Z-API retorna { value: "CODIGO" }
      const codigo = data.value ?? data.pairingCode ?? data.code ?? "";
      if (!codigo) {
        setErroConexao(`Resposta inesperada da Z-API: ${JSON.stringify(data)}`);
        return;
      }

      setPairingCode(String(codigo));
      iniciarPolling();
    } catch (err) {
      setErroConexao(`Erro de rede: ${err instanceof Error ? err.message : "desconhecido"}`);
    } finally {
      setSolicitando(false);
    }
  };

  const handleDesconectar = async () => {
    const ok = window.confirm("Deseja realmente desconectar o WhatsApp?");
    if (!ok) return;
    setDesconectando(true);
    try {
      await fetch("/api/zapi/disconnect", { method: "POST" });
      setPairingCode("");
      setTelefone("");
      await verificarStatus();
    } finally {
      setDesconectando(false);
    }
  };

  const copiarCodigo = async () => {
    await navigator.clipboard.writeText(pairingCode);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  return (
    <div className="p-8 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-black">Configurações</h1>
        <p className="text-sm text-black/40">Ajuste as configurações do sistema.</p>
      </div>

      {/* Card Alterar Senha */}
      <section className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-black/5">
          <div className="w-9 h-9 bg-black rounded-xl flex items-center justify-center shrink-0">
            <KeyRound className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-black">Alterar senha</h2>
            <p className="text-xs text-black/40">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={handleAlterarSenha} className="px-6 py-5 space-y-4">
          {/* Senha atual */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">Senha atual</label>
            <div className="relative">
              <input
                type={mostraSenhaAtual ? "text" : "password"}
                value={senhaAtual}
                onChange={(e) => setSenhaAtual(e.target.value)}
                placeholder="••••••••"
                required
                className={`${inputCls} pr-10`}
              />
              <button
                type="button"
                onClick={() => setMostraSenhaAtual((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-black/25 hover:text-black/50 transition"
              >
                {mostraSenhaAtual ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Nova senha */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">Nova senha</label>
            <div className="relative">
              <input
                type={mostraNovaSenha ? "text" : "password"}
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
                className={`${inputCls} pr-10`}
              />
              <button
                type="button"
                onClick={() => setMostraNovaSenha((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-black/25 hover:text-black/50 transition"
              >
                {mostraNovaSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirmar nova senha */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">Confirmar nova senha</label>
            <input
              type="password"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              placeholder="Repita a nova senha"
              required
              className={inputCls}
            />
          </div>

          {erroSenha && (
            <p className="text-sm text-red-500 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
              {erroSenha}
            </p>
          )}
          {sucessoSenha && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-100 px-4 py-3 rounded-xl flex items-center gap-2">
              <Check className="w-4 h-4" /> Senha alterada com sucesso.
            </p>
          )}

          <button
            type="submit"
            disabled={salvandoSenha}
            className="flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition disabled:opacity-50"
          >
            {salvandoSenha ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Salvando...</>
            ) : (
              "Alterar senha"
            )}
          </button>
        </form>
      </section>

      {/* Card WhatsApp */}
      <section className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        {/* Header do card */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-black/5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-black rounded-xl flex items-center justify-center shrink-0">
              <Smartphone className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-black">WhatsApp</h2>
              <p className="text-xs text-black/40">Instância: DuNada</p>
            </div>
          </div>

          {/* Badge de status */}
          <div className="flex items-center gap-2">
            {status === "loading" && (
              <Loader2 className="w-4 h-4 animate-spin text-black/30" />
            )}
            {status === "connected" && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                Conectado
              </span>
            )}
            {status === "disconnected" && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-black/40 bg-black/5 px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-black/20 rounded-full" />
                Desconectado
              </span>
            )}
            {status === "error" && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-red-600 bg-red-50 px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                Erro
              </span>
            )}
            <button
              onClick={verificarStatus}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-black hover:bg-black/5 transition"
              title="Atualizar status"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Corpo do card */}
        <div className="px-6 py-5">
          {/* Estado: conectado */}
          {status === "connected" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl">
                <Wifi className="w-5 h-5 text-green-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    WhatsApp conectado com sucesso.
                  </p>
                  <p className="text-xs text-green-600 mt-0.5">
                    {zapiInfo?.smartphoneConnected
                      ? "Smartphone online e sincronizado."
                      : "Instância ativa — smartphone offline."}
                  </p>
                </div>
              </div>
              <button
                onClick={handleDesconectar}
                disabled={desconectando}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-black/10 text-sm font-medium text-black/50 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition disabled:opacity-50"
              >
                {desconectando ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <LogOut className="w-4 h-4" />
                )}
                Desconectar WhatsApp
              </button>
            </div>
          )}

          {/* Estado: desconectado */}
          {(status === "disconnected" || status === "error") && !pairingCode && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-black/[0.02] rounded-xl">
                <WifiOff className="w-5 h-5 text-black/30 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-black/60">
                    Nenhum WhatsApp conectado.
                  </p>
                  <p className="text-xs text-black/40">
                    Informe o número abaixo para receber um código de pareamento.
                    Você precisará inserir esse código no WhatsApp em{" "}
                    <strong>Dispositivos conectados → Conectar com número de telefone</strong>.
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-black/60">
                  Número do WhatsApp
                </label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3.5 rounded-xl border border-black/10 bg-black/[0.02] text-sm text-black/40 shrink-0 select-none">
                    +55
                  </div>
                  <input
                    type="text"
                    placeholder="(11) 99999-9999"
                    value={telefone}
                    onChange={(e) => setTelefone(mascaraTelefone(e.target.value))}
                    className={inputCls}
                    maxLength={16}
                  />
                </div>
                <p className="text-xs text-black/30">
                  O código +55 do Brasil já está incluído automaticamente.
                </p>
              </div>

              {erroConexao && (
                <p className="text-sm text-red-500 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
                  {erroConexao}
                </p>
              )}

              <button
                onClick={handleSolicitarCodigo}
                disabled={solicitando}
                className="flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition disabled:opacity-50"
              >
                {solicitando ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Solicitando...
                  </>
                ) : (
                  "Solicitar código de pareamento"
                )}
              </button>
            </div>
          )}

          {/* Estado: código gerado, aguardando pareamento */}
          {pairingCode && status !== "connected" && (
            <div className="space-y-4">
              <div className="p-4 bg-black/[0.02] rounded-xl space-y-2">
                <p className="text-xs font-medium text-black/50">
                  No seu WhatsApp, acesse:
                </p>
                <ol className="text-xs text-black/40 space-y-1 list-decimal list-inside">
                  <li>Dispositivos conectados</li>
                  <li>Conectar com número de telefone</li>
                  <li>Insira o código abaixo</li>
                </ol>
              </div>

              {/* Código */}
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center justify-center gap-1.5 py-4 bg-black rounded-2xl">
                  {pairingCode.split("").map((char, i) => (
                    <span
                      key={i}
                      className="text-2xl font-bold text-white tracking-widest"
                    >
                      {char}
                    </span>
                  ))}
                </div>
                <button
                  onClick={copiarCodigo}
                  className="w-12 h-12 flex items-center justify-center rounded-xl border border-black/10 text-black/30 hover:text-black hover:bg-black/5 transition shrink-0"
                  title="Copiar código"
                >
                  {copiado ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>

              <div className="flex items-center gap-2 text-xs text-black/40">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Aguardando conexão...
              </div>

              <button
                onClick={() => { setPairingCode(""); setTelefone(""); }}
                className="text-xs text-black/30 hover:text-black transition"
              >
                Cancelar e tentar outro número
              </button>
            </div>
          )}

          {/* Loading inicial */}
          {status === "loading" && (
            <div className="flex items-center gap-2 text-sm text-black/30 py-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              Verificando conexão...
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
